import type {
  CampaignCreationCapabilities,
  SmartCreationStrategyBranchDecision,
  SmartCreationStrategyCapabilities,
  SmartCreationStrategyDiagnostic,
  SmartCreationStrategyOperation,
  SmartCreationStrategyRecommendation,
  SmartCreationStrategyTargetIntent,
  SmartCreationTargetingStrategyBranch,
  SmartCreationTargetingStrategyKind,
} from "@adpilot/shared";
import {
  effectiveSmartCreationStrategyLanes,
  smartCreationStrategyTargetLane,
} from "@adpilot/shared";
import { IconArrowLeft, IconEdit, IconRestore, IconTargetArrow } from "@tabler/icons-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";
import { AddedTargetsPane } from "./strategy-target-editor/AddedTargetsPane";
import {
  acceptedTargetSelection,
  buildOperations,
  canAcceptRecommendation,
  canRestoreSource,
  diagnosticTargetId,
  type EditableTargetValue,
  initialMatchType,
  type LocalTarget,
  type RestoreSource,
  respectsDecimalPrecision,
  statusFor,
  strategyLabelKey,
  type TargetRow,
  type TargetSourceFilter,
  type TargetStatusFilter,
  type TargetTypeFilter,
  targetFingerprint,
  targetLabel,
  targetSource,
  targetValue,
} from "./strategy-target-editor/strategy-target-model";
import { TargetCandidatesPane } from "./strategy-target-editor/TargetCandidatesPane";
import { TargetEntryForm } from "./strategy-target-editor/TargetEntryForm";
import { TargetInspector } from "./strategy-target-editor/TargetInspector";
import { useTargetEntryForm } from "./strategy-target-editor/use-target-entry-form";

interface StrategyTargetEditorProps {
  shopId: string;
  planRevision: number;
  branch: SmartCreationTargetingStrategyBranch;
  allBranches: SmartCreationTargetingStrategyBranch[];
  capabilities: CampaignCreationCapabilities;
  decision: SmartCreationStrategyBranchDecision;
  diagnostics: SmartCreationStrategyDiagnostic[];
  recommendation: SmartCreationStrategyRecommendation | null;
  strategyCapabilities: SmartCreationStrategyCapabilities;
  mutationPending?: boolean;
  onApply: (
    operations: SmartCreationStrategyOperation[],
    expectedRevision: number,
  ) => Promise<boolean>;
  onAcceptRecommendation: (
    kind: SmartCreationTargetingStrategyKind,
    expectedRevision: number,
  ) => Promise<boolean>;
  onCancel: () => void;
  onComplete: () => void;
  onDirtyChange: (dirty: boolean) => void;
}

export function StrategyTargetEditor({
  shopId,
  planRevision,
  branch: incomingBranch,
  allBranches,
  capabilities,
  decision,
  diagnostics,
  recommendation,
  strategyCapabilities,
  mutationPending = false,
  onApply,
  onAcceptRecommendation,
  onCancel,
  onComplete,
  onDirtyChange,
}: StrategyTargetEditorProps) {
  const { t } = useTranslation("smartCreation");
  const localIdRef = useRef(0);
  const entryValuesRef = useRef<HTMLTextAreaElement>(null);
  const [baseline, setBaseline] = useState(() => ({
    branch: structuredClone(incomingBranch),
    revision: planRevision,
  }));
  const branch = baseline.branch;
  const initialEntryMatchType = initialMatchType(branch);
  const initialEntryBid = String(capabilities.bid.min);
  const initialRecommendationTargetId = recommendation?.branches.find(
    (candidateBranch) => candidateBranch.kind === branch.kind,
  )?.targets[0]?.id;
  const [edits, setEdits] = useState<Record<string, EditableTargetValue>>({});
  const [additions, setAdditions] = useState<LocalTarget[]>([]);
  const [selectedTargetId, setSelectedTargetId] = useState(
    branch.targets[0]?.id ??
      (initialRecommendationTargetId ? `candidate:${initialRecommendationTargetId}` : null),
  );
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<TargetTypeFilter>("all");
  const [sourceFilter, setSourceFilter] = useState<TargetSourceFilter>("all");
  const [statusFilter, setStatusFilter] = useState<TargetStatusFilter>("all");
  const [laneFilter, setLaneFilter] = useState<"active" | "all">("active");
  const entryForm = useTargetEntryForm(shopId, initialEntryMatchType, initialEntryBid);
  const {
    entryType,
    setEntryType,
    entryValues,
    setEntryValues,
    entryMatchType,
    setEntryMatchType,
    entryBid,
    setEntryBid,
    setEntryError,
    entryMode,
    setEntryMode,
    categorySearch,
    setSelectedCategory,
  } = entryForm;
  const [restoreSource, setRestoreSource] = useState<RestoreSource | null>(null);

  const rows = useMemo<TargetRow[]>(() => {
    const persistedRecommendationIds = new Set(
      branch.targets.flatMap((target) =>
        target.provenance.mode === "entered" ? [] : [target.provenance.recommendationTargetId],
      ),
    );
    const representedFingerprints = new Set(
      branch.targets.flatMap((target) => [
        targetFingerprint(target.intent),
        ...(target.provenance.mode === "entered"
          ? []
          : [targetFingerprint(target.provenance.originalRecommendation.intent)]),
      ]),
    );
    const recommendationBranch = recommendation?.branches.find(
      (candidateBranch) => candidateBranch.kind === branch.kind,
    );
    return [
      ...branch.targets.map((target) => ({
        id: target.id,
        local: false as const,
        persisted: target,
        ...(edits[target.id] ?? targetValue(target)),
      })),
      ...(recommendationBranch?.targets ?? [])
        .filter(
          (target) =>
            !persistedRecommendationIds.has(target.id) &&
            !representedFingerprints.has(targetFingerprint(target.intent)) &&
            !additions.some((addition) => addition.id === `candidate:${target.id}`),
        )
        .map((target) => ({
          id: `candidate:${target.id}`,
          local: false as const,
          candidate: target,
          intent: target.intent,
          bid: target.bid,
          included: false,
        })),
      ...additions,
    ];
  }, [additions, branch.kind, branch.targets, edits, recommendation]);
  const selectedTarget = rows.find((target) => target.id === selectedTargetId) ?? null;
  const selectedBidInvalid = Boolean(
    selectedTarget &&
      (selectedTarget.bid < capabilities.bid.min ||
        selectedTarget.bid > capabilities.bid.max ||
        !respectsDecimalPrecision(selectedTarget.bid, capabilities.bid.decimals)),
  );
  const branchDiagnostics = diagnostics.filter(
    (item) => item.strategyKind === null || item.strategyKind === branch.kind,
  );
  const activeBlockingDiagnostics = branchDiagnostics.filter((item) => {
    if (!item.blocking) return false;
    if (!item.targetId) return true;
    return rows.find((row) => diagnosticTargetId(row) === item.targetId)?.included !== false;
  });
  const localValidationCount = rows.filter(
    (row) =>
      row.included &&
      (row.bid < capabilities.bid.min ||
        row.bid > capabilities.bid.max ||
        !respectsDecimalPrecision(row.bid, capabilities.bid.decimals) ||
        (row.intent.type === "category" && !strategyCapabilities.targetIntents.category.supported)),
  ).length;
  const externalFingerprints = new Set(
    allBranches
      .filter((candidateBranch) => candidateBranch.kind !== branch.kind)
      .flatMap((candidateBranch) => candidateBranch.targets)
      .map((target) => targetFingerprint(target.intent)),
  );
  const fingerprints = new Map<string, number>();
  for (const row of rows) {
    if ((row.candidate && !row.local) || !row.included) continue;
    const fingerprint = targetFingerprint(row.intent);
    fingerprints.set(fingerprint, (fingerprints.get(fingerprint) ?? 0) + 1);
  }
  const duplicatedFingerprints = new Set(
    [...fingerprints.entries()]
      .filter(([fingerprint, count]) => count > 1 || externalFingerprints.has(fingerprint))
      .map(([fingerprint]) => fingerprint),
  );
  const duplicateCount = duplicatedFingerprints.size;
  const blockingCount = activeBlockingDiagnostics.length + localValidationCount + duplicateCount;
  const stagedTargetOperations = buildOperations(branch.kind, branch, rows, null);
  const restoreAllowed = canRestoreSource(stagedTargetOperations);
  const operations = restoreSource
    ? buildOperations(branch.kind, branch, rows, restoreSource)
    : stagedTargetOperations;
  const entryFormDirty =
    entryValues.trim().length > 0 ||
    entryMatchType !== initialEntryMatchType ||
    entryBid !== initialEntryBid;
  const localDirty = operations.length > 0 || entryFormDirty;
  // Compare staged edits with the opening snapshot, not changing query props.
  // Reverted edits are clean and may rebase; genuine edits await CAS conflict handling.
  if (
    !localDirty &&
    (planRevision !== baseline.revision ||
      JSON.stringify(incomingBranch.targets) !== JSON.stringify(branch.targets))
  ) {
    setBaseline({ branch: structuredClone(incomingBranch), revision: planRevision });
    setEdits({});
    setAdditions([]);
    setEntryMatchType(initialMatchType(incomingBranch));
  }
  const recommendationAcceptAllowed = canAcceptRecommendation(operations) && !entryFormDirty;
  const selectedCount = rows.filter((target) => target.included).length;
  const enabledLanes = effectiveSmartCreationStrategyLanes(branch);
  function laneActive(target: TargetRow): boolean {
    // Old plans had no lane selection. Preserve their editor availability while
    // explicit lane choices distinguish staged values from executable targets.
    return (
      !branch.enabledLanes ||
      enabledLanes.includes(smartCreationStrategyTargetLane(branch.kind, target.intent))
    );
  }
  const candidateCount = rows.filter(
    (target) => !target.included && (laneFilter === "all" || laneActive(target)),
  ).length;
  const query = search.trim().toLocaleLowerCase();
  const filteredRows = rows.filter((target) => {
    const source = targetSource(target);
    const status = statusFor(
      target,
      branchDiagnostics,
      duplicatedFingerprints.has(targetFingerprint(target.intent)),
    );
    return (
      (!query || targetLabel(target.intent).toLocaleLowerCase().includes(query)) &&
      (typeFilter === "all" || target.intent.type === typeFilter) &&
      (sourceFilter === "all" || source === sourceFilter) &&
      (statusFilter === "all" ||
        (statusFilter === "selected" && target.included) ||
        (statusFilter === "excluded" && !target.included) ||
        (statusFilter === "issue" && status.variant === "destructive"))
    );
  });
  const candidateRows = filteredRows.filter((target) => laneFilter === "all" || laneActive(target));
  const addedRows = filteredRows.filter((target) => target.included);

  useEffect(() => {
    setSelectedTargetId((current) => acceptedTargetSelection(branch, current));
  }, [branch]);

  useEffect(() => {
    onDirtyChange(localDirty);
  }, [localDirty, onDirtyChange]);

  useEffect(() => {
    if (entryMode) entryValuesRef.current?.focus();
  }, [entryMode]);

  function updateTarget(id: string, patch: Partial<EditableTargetValue>): void {
    const current = rows.find((target) => target.id === id);
    if (!current) return;
    if (current.candidate && !current.local) return;
    onDirtyChange(true);
    setRestoreSource(null);
    if (current.local) {
      setAdditions((items) => items.map((item) => (item.id === id ? { ...item, ...patch } : item)));
      return;
    }
    setEdits((currentEdits) => ({
      ...currentEdits,
      [id]: { intent: current.intent, bid: current.bid, included: current.included, ...patch },
    }));
  }

  function setTargetIncluded(target: TargetRow, included: boolean): void {
    if (mutationPending) return;
    if (branch.kind === "automatic" && !included && selectedCount <= 1) return;
    if (!target.candidate || target.local) {
      updateTarget(target.id, { included });
      return;
    }
    if (!included) return;
    // The existing operation records an explicit user-selected value, not an
    // accepted recommendation snapshot. Keep the source only as local audit.
    onDirtyChange(true);
    setRestoreSource(null);
    setAdditions((current) => [
      ...current,
      {
        id: target.id,
        local: true,
        candidate: target.candidate,
        intent: target.intent,
        bid: target.bid,
        included: true,
      },
    ]);
  }

  function addEnteredTargets(): void {
    const values = entryValues
      .split(/[\n,]/)
      .map((value) => value.trim())
      .filter(Boolean);
    const bid = Number(entryBid);
    if (
      values.length === 0 ||
      !Number.isFinite(bid) ||
      bid < capabilities.bid.min ||
      bid > capabilities.bid.max ||
      !respectsDecimalPrecision(bid, capabilities.bid.decimals)
    ) {
      setEntryError(t("strategy.editor.blocking", { count: 1 }));
      return;
    }
    if (
      entryType === "product" &&
      values.some((value) => !/^[A-Z0-9]{10}$/.test(value.toUpperCase()))
    ) {
      setEntryError(t("strategy.editor.invalidAsin"));
      return;
    }
    if (entryType === "category" && values.some((value) => !/^\d{1,128}$/.test(value))) {
      setEntryError(t("strategy.editor.invalidCategoryId"));
      return;
    }
    const existing = new Set([
      ...rows.map((target) => targetFingerprint(target.intent)),
      ...externalFingerprints,
    ]);
    const next: LocalTarget[] = [];
    for (const value of values) {
      const intent: SmartCreationStrategyTargetIntent =
        entryType === "keyword"
          ? { type: "keyword", keyword: value, matchType: entryMatchType }
          : entryType === "category"
            ? { type: "category", categoryId: value }
            : {
                type: "product",
                asin: value.toUpperCase(),
                matchType: branch.kind === "research" ? "expanded" : "exact",
              };
      if (existing.has(targetFingerprint(intent))) {
        setEntryError(t("strategy.editor.duplicateEntry"));
        return;
      }
      existing.add(targetFingerprint(intent));
      localIdRef.current += 1;
      next.push({
        id: `local:${branch.kind}:${localIdRef.current}`,
        local: true,
        intent,
        bid,
        included: true,
      });
    }
    onDirtyChange(true);
    setAdditions((items) => [...items, ...next]);
    setRestoreSource(null);
    setSelectedTargetId(next[0]?.id ?? selectedTargetId);
    setEntryType("keyword");
    setEntryValues("");
    categorySearch.setQuery("");
    setSelectedCategory(null);
    setEntryMatchType(initialEntryMatchType);
    setEntryBid(initialEntryBid);
    setEntryError(null);
    setEntryMode(false);
  }

  function inspectTarget(id: string): void {
    setSelectedTargetId(id);
    setEntryMode(false);
  }

  function discardAndClose(): void {
    onCancel();
  }

  function removeLocalTarget(id: string): void {
    onDirtyChange(true);
    setRestoreSource(null);
    setAdditions((items) => items.filter((item) => item.id !== id));
    setSelectedTargetId((current) => (current === id ? null : current));
  }

  async function applyChanges(): Promise<void> {
    if (operations.length === 0 || blockingCount > 0 || entryFormDirty) return;
    if (await onApply(operations, baseline.revision)) onComplete();
  }

  async function acceptRecommendation(): Promise<void> {
    if (!recommendationAcceptAllowed) return;
    await onAcceptRecommendation(branch.kind, baseline.revision);
  }

  const recommendationEvidence =
    decision.recommendationSnapshot?.recommendation.evidence ?? recommendation?.evidence ?? null;

  return (
    <fieldset
      disabled={mutationPending}
      aria-busy={mutationPending}
      aria-labelledby="smart-creation-target-editor-title"
      data-strategy-subview="targets"
      className="flex min-h-full min-w-0 flex-col border-0 bg-surface p-0"
    >
      <div className="flex flex-1 flex-col gap-4 py-2">
        <div className="flex items-end justify-between gap-6">
          <div className="flex items-start gap-3">
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label={t("strategy.editor.back")}
              onClick={discardAndClose}
            >
              <IconArrowLeft />
            </Button>
            <div>
              <h2
                id="smart-creation-target-editor-title"
                tabIndex={-1}
                className="text-base font-semibold text-ink outline-none"
              >
                {t("strategy.editor.title", { strategy: t(strategyLabelKey(branch.kind)) })}
              </h2>
              <p className="mt-1 max-w-[760px] text-[13px] leading-5 text-muted-foreground">
                {t("strategy.editor.scope")}
              </p>
            </div>
          </div>
          <span aria-live="polite" className="font-mono text-xs text-muted-foreground">
            {t("strategy.editor.count", { selected: selectedCount, pending: candidateCount })}
          </span>
        </div>

        {branch.kind !== "automatic" && !strategyCapabilities.targetIntents.category.supported ? (
          <p className="text-xs text-muted-foreground">
            {t("strategy.editor.categoryUnavailable")}
          </p>
        ) : null}

        {branch.kind === "automatic" ? (
          <p className="text-xs text-muted-foreground">{t("strategy.editor.autoReadonly")}</p>
        ) : null}

        <div className="grid min-h-[442px] flex-1 grid-cols-1 gap-4 xl:grid-cols-2">
          <TargetCandidatesPane
            branch={branch}
            candidateRows={candidateRows}
            selectedTargetId={selectedTargetId}
            selectedCount={selectedCount}
            blockingCount={blockingCount}
            mutationPending={mutationPending}
            search={search}
            laneFilter={laneFilter}
            typeFilter={typeFilter}
            sourceFilter={sourceFilter}
            statusFilter={statusFilter}
            isLaneActive={laneActive}
            onSearchChange={setSearch}
            onLaneFilterChange={setLaneFilter}
            onTypeFilterChange={setTypeFilter}
            onSourceFilterChange={setSourceFilter}
            onStatusFilterChange={setStatusFilter}
            onEnterTarget={() => setEntryMode(true)}
            onInspectTarget={inspectTarget}
            onTargetIncludedChange={setTargetIncluded}
          />

          <AddedTargetsPane
            branchKind={branch.kind}
            addedRows={addedRows}
            selectedTargetId={selectedTargetId}
            selectedCount={selectedCount}
            capabilities={capabilities}
            mutationPending={mutationPending}
            isLaneActive={laneActive}
            onInspectTarget={inspectTarget}
            onUpdateTarget={updateTarget}
            onTargetIncludedChange={setTargetIncluded}
            onRemoveAll={() => {
              for (const target of rows.filter((row) => row.included))
                setTargetIncluded(target, false);
            }}
          />

          <aside
            className="rounded-md border border-line bg-surface p-4 xl:col-span-2"
            aria-labelledby="smart-creation-target-inspector-title"
          >
            {entryMode ? (
              <TargetEntryForm
                form={entryForm}
                entryValuesRef={entryValuesRef}
                branchKind={branch.kind}
                categorySupported={strategyCapabilities.targetIntents.category.supported}
                capabilities={capabilities}
                mutationPending={mutationPending}
                initialEntryMatchType={initialEntryMatchType}
                initialEntryBid={initialEntryBid}
                stagedOperationCount={operations.length}
                onDirtyChange={onDirtyChange}
                onSubmit={addEnteredTargets}
              />
            ) : selectedTarget ? (
              <TargetInspector
                selectedTarget={selectedTarget}
                branchKind={branch.kind}
                capabilities={capabilities}
                mutationPending={mutationPending}
                selectedBidInvalid={selectedBidInvalid}
                recommendationEvidence={recommendationEvidence}
                recommendationAcceptAllowed={recommendationAcceptAllowed}
                restoreAllowed={restoreAllowed}
                branchDiagnostics={branchDiagnostics}
                onUpdateTarget={updateTarget}
                onRemoveLocalTarget={removeLocalTarget}
                onAcceptRecommendation={acceptRecommendation}
              />
            ) : (
              <Empty className="h-full py-10">
                <EmptyMedia variant="icon">
                  <IconTargetArrow />
                </EmptyMedia>
                <EmptyHeader>
                  <EmptyTitle className="text-base">
                    {t("strategy.editor.inspectorTitle")}
                  </EmptyTitle>
                  <EmptyDescription className="text-xs">
                    {t("strategy.editor.emptyDescription")}
                  </EmptyDescription>
                </EmptyHeader>
              </Empty>
            )}
          </aside>
        </div>
      </div>

      <footer className="sticky bottom-0 flex h-20 shrink-0 items-center justify-between border-t border-line bg-table-footer px-7">
        <div aria-live="polite" className="text-xs text-muted-foreground">
          {entryFormDirty
            ? t("strategy.editor.addEntryBeforeApply")
            : blockingCount > 0
              ? t("strategy.editor.blocking", { count: blockingCount })
              : restoreSource
                ? t(
                    restoreSource === "recommended"
                      ? "strategy.editor.restoreRecommended"
                      : "strategy.editor.restoreEntered",
                  )
                : null}
        </div>
        <div className="flex items-center gap-2.5">
          {decision.recommendationSnapshot && decision.mode !== "accepted" ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={!restoreAllowed || entryFormDirty}
              onClick={() => {
                onDirtyChange(true);
                setRestoreSource("recommended");
              }}
            >
              <IconRestore data-icon="inline-start" />
              {t("strategy.editor.restoreRecommended")}
            </Button>
          ) : null}
          {decision.mode !== "entered" ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={!restoreAllowed || entryFormDirty}
              onClick={() => {
                onDirtyChange(true);
                setRestoreSource("entered");
              }}
            >
              <IconEdit data-icon="inline-start" />
              {t("strategy.editor.restoreEntered")}
            </Button>
          ) : null}
          <Button type="button" variant="outline" onClick={discardAndClose}>
            {t("strategy.editor.cancel")}
          </Button>
          <Button
            type="button"
            disabled={operations.length === 0 || blockingCount > 0 || entryFormDirty}
            onClick={() => void applyChanges()}
          >
            {operations.length === 0
              ? t("strategy.editor.noChanges")
              : t("strategy.editor.apply", { count: operations.length })}
          </Button>
        </div>
      </footer>
    </fieldset>
  );
}

export {
  acceptedTargetSelection as getAcceptedStrategyTargetSelection,
  buildOperations as buildStrategyTargetOperations,
  canAcceptRecommendation as canAcceptStrategyRecommendation,
  canRestoreSource as canRestoreStrategyTargetSource,
  targetFingerprint,
  targetSource as getStrategyTargetSource,
};
